import { useQuery, useQueryClient } from "@tanstack/react-query";
import { some } from "lodash";
import { Collection } from "../../types/collections.types";
import { EEG_SERVICES, FIRST_EEG_SERVICE, FOLLOWUP_SERVICE, GENERAL_EEG_SERVICE, NFB_SERVICE, PSY_SERVICE } from "../../utils/constants";
import { getRecords } from "../useRecords";
import { getRecord } from "../useRecord";
import { activeServicesProvidedFilter, plannedServicesProvidedFilter } from "../../screens/treatments/treatment-prescription/utils";
import { SessionsSummary } from "./types";
import {
    getSessions,
    getOrderItems,
    createSessionsFilter,
    createOrderItemsFilter,
    getBookedSessions,
    getBookedSessionsForMultipleServices,
    getLatestBookedSessionDate,
    getLatestBookedSessionDateForMultipleServices,
    getPrescribedMaxSessions,
    getPrescribedMaxSessionsForMultipleServices,
    getPreferredSchedule,
} from "./utils";

export const useClientSessionsSummary = (parentId: string | number = "", treatmentId?: string | number) => {
    const queryClient = useQueryClient();

    const { collections } = queryClient.getQueryData(["collections"]) as { collections: Collection[] };
    const ordersExistInCollection = some(collections, (collection: Collection) => collection.collection === "order_items");

    return useQuery({
        queryKey: [`client-sessions-summary-${parentId || ""}-${treatmentId || ""}`],
        queryFn: () => getClientSessionSummary(parentId, ordersExistInCollection, treatmentId),
    });
};

const getClientSessionSummary = async (
    parentId: string | number = "",
    ordersExistInCollection: boolean,
    treatmentId?: string | number
): Promise<SessionsSummary | undefined> => {
    if (!parentId) return null;

    const sessionsFilter = createSessionsFilter(parentId, treatmentId);

    const sessionsPromise = getRecords("sessions", "status,start_date,service.id,service.category", {
        extraOptions: {
            filter: sessionsFilter,
        },
    });

    const orderItemsPromise = ordersExistInCollection
        ? getRecords("order_items", "quantity,service_variant.service.category", {
              extraOptions: {
                  filter: createOrderItemsFilter(parentId, treatmentId),
              },
          })
        : Promise.resolve(null);

    const activeTreatmentId = treatmentId || parentId;

    const treatmentPrescriptionsPromise = treatmentId
        ? getRecord("treatments", activeTreatmentId, "treatment_status_new,treatment_prescription.*,treatment_prescription.services_provided.*,treatment_prescription.services_prescribed.*,treatment_prescription.services_prescribed.service.*")
        : getRecords("treatments", "treatment_prescription.*,treatment_prescription.services_provided.*,treatment_prescription.services_prescribed.*,treatment_prescription.services_prescribed.service.*", {
              extraOptions: {
                  filter: {
                      client: { _eq: parentId },
                      treatment_prescription: { _nnull: true },
                  },
              },
          });

    const [sessionsResult, orderItemsResult, treatmentResult] = await Promise.all([
        sessionsPromise,
        orderItemsPromise,
        treatmentPrescriptionsPromise,
    ]);

    const sessions = sessionsResult.items;
    const orderItems = orderItemsResult?.items;
    const treatmentPrescriptions = treatmentId
        ? (treatmentResult as any)?.item?.treatment_prescription?.[0] ?? null
        : (treatmentResult as any)?.items?.[0]?.treatment_prescription?.[0] ?? null;

    const nfbDone = getSessions(sessions, "neurofeedback", { sessionDone: true });
    const nfbBought = orderItems ? getOrderItems(orderItems, "neurofeedback") : 0;

    const psyDone = getSessions(sessions, "psicologia", { sessionDone: true });
    const psyBought = orderItems ? getOrderItems(orderItems, "psicologia") : 0;

    const nfbBooked = getBookedSessions(sessions, NFB_SERVICE);
    const psyBooked = getBookedSessions(sessions, PSY_SERVICE);
    const evaluationsBooked = getBookedSessionsForMultipleServices(sessions, [GENERAL_EEG_SERVICE]);

    const nfbLatestBookedDate = getLatestBookedSessionDate(sessions, NFB_SERVICE);
    const psyLatestBookedDate = getLatestBookedSessionDate(sessions, PSY_SERVICE);
    const evaluationsLatestBookedDate = getLatestBookedSessionDateForMultipleServices(sessions, EEG_SERVICES);

    const servicesPrescribed = treatmentPrescriptions?.services_prescribed || [];
    const nfbPrescribed = getPrescribedMaxSessions(servicesPrescribed, NFB_SERVICE);
    const psyPrescribed = getPrescribedMaxSessions(servicesPrescribed, PSY_SERVICE);
    const evaluationsPrescribed = getPrescribedMaxSessionsForMultipleServices(servicesPrescribed, EEG_SERVICES);

    const activeTreatments = treatmentPrescriptions?.services_provided
        .filter(activeServicesProvidedFilter)
        .map((treatment: any) => ({ service: treatment.service, status: "active" })) || []

    const plannedTreatments = treatmentPrescriptions?.services_provided
        .filter(plannedServicesProvidedFilter)
        .map((treatment: any) => ({ service: treatment.service, status: "planned" })) || []

    const onGoingTreatments = [...activeTreatments, ...plannedTreatments]

    const nfbDebt = nfbDone - nfbBought;
    const psyDebt = psyDone - psyBought;
    const totalDebt = nfbDebt + psyDebt;

    const servicesProvided = treatmentPrescriptions?.services_provided || [];
    const preferredSchedule = getPreferredSchedule(sessions, servicesProvided);

    const sessionsData = {
        nfbBought,
        nfbDone,
        psyBought,
        psyDone,
        nfbNext7Days: getSessions(sessions, "neurofeedback", { next7Days: true }),
        psyNext7Days: getSessions(sessions, "psicologia", { next7Days: true }),
        evaluationsBought: orderItems ? getOrderItems(orderItems, "avaliacoes") : 0,
        evaluationsDone: getSessions(sessions, "avaliacoes", { sessionDone: true }),
        evaluationsFirstDone: getSessions(sessions, "avaliacoes", { sessionDone: true }, FIRST_EEG_SERVICE),
        followUpDone: getSessions(sessions, null, { sessionDone: true }, FOLLOWUP_SERVICE),
        totalDebt,
        activeTreatment: onGoingTreatments,
        nfbBooked,
        psyBooked,
        evaluationsBooked,
        nfbPrescribed,
        psyPrescribed,
        evaluationsPrescribed,
        nfbLatestBookedDate,
        psyLatestBookedDate,
        evaluationsLatestBookedDate,
        preferredSchedule,
    };
    return sessionsData;
};
